import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';
import { Flex, Text } from '@qga/roo-ui/components';
import { getQueryPayWith } from 'store/router/routerSelectors';
import usePointsConverters from 'hooks/points/usePointsConverters';
import Currency from 'components/Currency';
import OfferGuestsText from 'components/OfferGuestsText';
import { VPP_CAMPAIGN } from 'config/constants';
import useCampaignPriceMessage from 'components/CampaignPriceMessage/useCampaignPriceMessage';
import { getLuxePointsLevels } from 'store/pointsBurnLuxe/pointsBurnLuxeSelectors';

export const Wrapper = styled(Flex)`
  border-left: 3px solid ${themeGet('colors.brand.primary')};
  padding-left: ${themeGet('space.3')};
  margin-bottom: 16px;
  flex-direction: column;
  align-items: flex-start;
`;

export const SavingsText = styled(Text)`
  color: ${themeGet('colors.darkGreen')};
  font-size: ${themeGet('fontSizes.sm')};
  font-weight: bold;
  text-transform: uppercase;
`;
const UppercaseText = styled(Text)`
  display: block;
  font-weight: bold;
  text-transform: uppercase;
  color: ${themeGet('colors.darkGreen')};
  font-size: ${themeGet('fontSizes.sm')};
`;

const ExclusiveOffer = ({ adults, kids, infants, numNights, totalPrice, totalDiscount }) => {
  const queryPayWith = useSelector(getQueryPayWith);
  const getLuxeLevels = useSelector(getLuxePointsLevels);
  const currentLevels = usePointsConverters(getLuxeLevels);
  const previousTiers = usePointsConverters(VPP_CAMPAIGN.originalTier?.tiers);
  const isCurrencyCash = queryPayWith === 'cash';
  const totalGuests = parseInt(adults || 0) + parseInt(kids || 0) + parseInt(infants || 0);
  const hasValuedAtPrice = parseInt(totalDiscount?.amount) > 0 && totalPrice.amount !== totalDiscount.amount;

  const { savings, hasSavings } = useMemo(() => {
    if (hasValuedAtPrice && totalPrice.currency && totalDiscount.currency !== 'PTS') {
      const amount = Math.floor(totalDiscount.amount - totalPrice.amount);
      const savings = { amount, currency: totalPrice.currency };
      const hasSavings = !Number.isNaN(savings);
      return { savings, hasSavings };
    }
    return { hasSavings: false };
  }, [hasValuedAtPrice, totalDiscount, totalPrice]);

  const { finalPrice, valuedAtPrice } = useMemo(() => {
    const isCorrectCurrency = totalPrice?.currency === 'PTS' && queryPayWith === 'points';
    const currentPoints = !isCorrectCurrency ? currentLevels.convertCashToPoints({ cash: totalPrice?.amount }) : totalPrice?.amount;
    const previousPoints = previousTiers.convertCashToPoints({ cash: totalPrice?.amount });
    const finalPrice = {
      amount: isCurrencyCash ? totalPrice?.amount : currentPoints,
      currency: isCurrencyCash ? totalPrice?.currency : 'PTS',
      termsConditionsSymbol: totalPrice?.termsConditionsSymbol,
    };
    const valuedAtPrice = hasValuedAtPrice && {
      amount: isCurrencyCash ? totalDiscount.amount : previousPoints,
      currency: isCurrencyCash ? totalDiscount.currency : 'PTS',
      termsConditionsSymbol: totalDiscount?.termsConditionsSymbol,
    };
    return { finalPrice, valuedAtPrice };
  }, [currentLevels, hasValuedAtPrice, isCurrencyCash, previousTiers, queryPayWith, totalDiscount, totalPrice]);

  const { showCampaignPriceMessage, campaignPriceMessage } = useCampaignPriceMessage({
    currency: finalPrice.currency,
    offerType: 'exclusive-offer',
    fallback: true,
  });

  return (
    <Wrapper>
      <OfferGuestsText guests={totalGuests} numberOfNights={numNights} mb={1} />
      <Currency
        {...finalPrice}
        alignItems="flex-start"
        roundToCeiling
        fontSize={36}
        fontWeight="bold"
        color="greys.charcoal"
        data-testid="total-to-pay"
        alignCurrency="superscript"
      />
      {hasValuedAtPrice && hasSavings && isCurrencyCash && (
        <Flex alignItems="center">
          <Text pr={1} fontSize="sm" color="greys.steel" data-testid="offer-valued-at">
            Valued at <Currency {...valuedAtPrice} roundToCeiling fontSize="sm" hideCurrency color="greys.steel" exclusiveOffer="true" />
          </Text>
          <SavingsText ml={1} data-testid="offer-savings">
            Save <Currency {...savings} hideCurrency fontWeight="bold" fontSize="sm" roundToCeiling exclusiveOffer="true" />
          </SavingsText>
        </Flex>
      )}
      {hasValuedAtPrice && !isCurrencyCash && showCampaignPriceMessage && (
        <UppercaseText data-testid="campaign-price-message">{campaignPriceMessage}</UppercaseText>
      )}
    </Wrapper>
  );
};

ExclusiveOffer.propTypes = {
  adults: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  kids: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  infants: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  numNights: PropTypes.number,
  totalPrice: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
    termsConditionsSymbol: PropTypes.string,
  }),
  totalDiscount: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
    termsConditionsSymbol: PropTypes.string,
  }),
};

export default ExclusiveOffer;
